/* eslint-disable @angular-eslint/prefer-inject */
import { ZoneDataService } from '../infrastructure/zones/zone.data.service';
import {
    isLocationIdentifier,
    ZoneCreate,
    ZoneDto,
    ZoneRead,
    ZoneSearchParams,
} from '../entities/zone.dto';
import { ZoneStore } from '../infrastructure/zones/zone.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { ZoneMapper } from '../infrastructure/zones/zone.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    EntitySaveOptions,
    IBarcodeSearchFacade,
    IManageEntityRoutes,
    IQueryParams,
    SafeAny,
} from '@pf/shared-common';
import {
    asArraySafe,
    asNumberSafe,
    getTempId,
    shallowCopyArray,
} from '@pf/shared-utility';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { WarehouseStructure } from '../entities/warehouse.dto';
import { ZoneSlotService } from './zone-slot.service';
import { WarehouseFacade } from './warehouse.facade';
import { ResourceAuth } from '@control-tower/platform-wms';

export type ZoneType = {
    displayName: string;
    type: string;
    level: number;
    last?: boolean;
};

export type ConflictingZoneResult = {
    error: boolean;
    message: string;
};

export function locationIdentifiersKey(
    locationIdentifiers?: ZoneDto['locationIdentifiers'] | null
) {
    return asArraySafe(locationIdentifiers)
        .sort((a, b) => asNumberSafe(a.level) - asNumberSafe(b.level))
        .map(l => l.name)
        .join('-');
}

export function zoneAndSlotBarcodesFromSlotBarcode(
    slotBarcode?: string | null
) {
    if (!slotBarcode) {
        return '';
    }
    const zoneBarcodeStartIndex = slotBarcode.indexOf('Z-');
    return slotBarcode.substring(zoneBarcodeStartIndex);
}

export function parentLocationIdentifierKey(
    locationIdentifiers?: ZoneDto['locationIdentifiers'] | null
) {
    return asArraySafe(locationIdentifiers)
        .sort((a, b) => asNumberSafe(a.level) - asNumberSafe(b.level))
        .slice(0, -1)
        .map(l => l.name)
        .join('-');
}

export function getZoneTypeInfo(
    zone: ZoneDto,
    zoneType: ZoneType
): { name?: string; barcode?: string } {
    if (zone.slots?.length) {
        return {
            name: zone.name || '',
            barcode: zone.barcode,
        };
    }
    const locationIdentifier = asArraySafe(zone.locationIdentifiers).find(
        l => asNumberSafe(l.level) === zoneType.level
    );

    return {
        name: locationIdentifier?.name,
        barcode: '',
    };
}

@Injectable()
export class ZoneFacade
    extends AbstractManageEntityFacade<
        ZoneDto,
        ZoneRead,
        ZoneCreate,
        ZoneSearchParams
    >
    implements IBarcodeSearchFacade<ZoneDto>
{
    private readonly _warehouseFacade = inject(WarehouseFacade);
    private readonly _zoneSlotService = inject(ZoneSlotService);
    nameField: keyof ZoneDto = 'name';

    // TODO inject zone types from config
    private readonly zoneTypes: ZoneType[] = [
        { type: 'BusinessUnit', displayName: 'Business Unit', level: 1 },
        { type: 'Zone', displayName: 'Zone', level: 2 },
        { type: 'Aisle', displayName: 'Aisle', level: 3 },
        { type: 'Bay', displayName: 'Bay', level: 4 },
        { type: 'Level', displayName: 'Level', level: 5, last: true },
    ];

    get zoneTypesSorted() {
        return shallowCopyArray(this.zoneTypes).sort(
            (a, b) => a.level - b.level
        );
    }

    constructor(
        dataService: ZoneDataService,
        store: ZoneStore,
        mapper: ZoneMapper
    ) {
        super(
            dataService,
            null as unknown as IManageEntityRoutes,
            store,
            mapper
        );
    }

    override getDefaultSearchParams$(
        params: IQueryParams<ZoneDto>
    ): Observable<IQueryParams<ZoneDto>> {
        return this._warehouseFacade.addDefaultWarehouseParam(params);
    }

    override add() {
        this.store.resetActiveEntity();
    }

    override manage() {
        this.store.resetActiveEntity();
    }

    override edit(entityId: string): void {
        this.store.setActiveEntity(entityId);
    }

    override save$(
        dto: ZoneDto,
        options?: EntitySaveOptions<ZoneDto>
    ): Observable<ZoneDto> {
        if (isLocationIdentifier(dto)) {
            return this.updateMatchingLocationIdentifiers$(dto);
        }
        return of(dto).pipe(
            map(zone => {
                zone.barcode = this._zoneSlotService.getZoneBarcode(zone);
                return zone;
            }),
            switchMap(zone => {
                return super.save$(zone, options);
            })
        );
    }

    searchByBarcode$(barcodeText: string): Observable<ZoneDto> {
        return this.search$({
            pageIndex: 1,
            pageSize: 1,
            filters: {
                barcode: barcodeText,
            },
        }).pipe(map(results => results.data[0]));
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ZoneDto, string>> {
        return { name: searchText };
    }

    getZoneTypeOfZone(
        zoneOrId: string | ZoneDto | null
    ): ZoneType | null | undefined {
        if (!zoneOrId) {
            return null;
        }
        const zone =
            typeof zoneOrId === 'string'
                ? this.getById(zoneOrId)
                : (zoneOrId as ZoneDto);

        if (!zone) {
            this.logger.error('Zone not found in store!');
            return null;
        }

        let level = 1;
        if (zone.locationIdentifiers?.length) {
            level =
                asArraySafe(zone.locationIdentifiers).sort(
                    (a, b) => asNumberSafe(b.level) - asNumberSafe(a.level)
                )[0].level || 1;
        }

        return this.getZoneTypeByLevel(level);
    }

    getZoneTypeByName(zoneType: string) {
        return this.zoneTypes.find(type => type.type === zoneType);
    }

    getZoneTypeByLevel(level: number): ZoneType | undefined {
        return this.zoneTypes.find(type => type.level === level);
    }

    getZoneByTypeAndParentId(
        warehouseId: string,
        zoneType: ZoneType,
        parentZoneId?: string | null,
        parentIdentifiers?: ZoneDto['locationIdentifiers']
    ): ZoneDto {
        const parentZone = parentZoneId ? this.getById(parentZoneId) : null;
        const parentZoneType = this.getZoneTypeOfZone(parentZone);

        // This shouldn't happen unless there is a code/configuration issue
        if (parentZone && !parentZoneType) {
            this.logger.error('Zone information is misconfigured!');
            return new ZoneDto({
                warehouseId,
                warehouse: { id: warehouseId },
            });
        }

        // If the parent zone is not a temp id and the parent zone type is less than the zone type
        // then return the parent zone to be updated with the new location identifiers
        if (
            parentZone &&
            parentZoneType &&
            !isLocationIdentifier(parentZone) &&
            parentZoneType.level < zoneType.level
        ) {
            return parentZone;
        }

        // Otherwise return a new zone with the parent zone's location identifiers
        return new ZoneDto({
            ...(parentZone || {}),
            id: '',
            warehouseId,
            warehouse: { id: warehouseId },
            name: '',
            barcode: '',
            customFields: [],
            zonePriority: this.getNextPriority(warehouseId, parentZone),
            locationIdentifiers: asArraySafe(
                parentZone ? parentZone.locationIdentifiers : parentIdentifiers
            ).filter(l => asNumberSafe(l.level) < zoneType.level),
        });
    }

    getZonesWithMatchingLocationIdentifiers(
        warehouseId: string,
        locationIdentifiers?: ZoneDto['locationIdentifiers'] | null
    ) {
        const key = locationIdentifiersKey(locationIdentifiers);

        return this.store
            .filter(
                (z: ZoneRead) =>
                    !isLocationIdentifier(z) &&
                    z.warehouseId === warehouseId &&
                    !z.isDeleted &&
                    locationIdentifiersKey(z.locationIdentifiers).startsWith(
                        key
                    )
            )
            .map(z => this.mapper.toDTO(z));
    }

    getZonesByPriority(warehouseId: string) {
        return this.store
            .filter(
                (zone: ZoneRead) =>
                    zone.warehouseId === warehouseId &&
                    !zone.isDeleted &&
                    !isLocationIdentifier(zone)
            )
            .map(z => this.mapper.toDTO(z))
            .sort(
                (a, b) =>
                    asNumberSafe(a.zonePriority) - asNumberSafe(b.zonePriority)
            );
    }

    updateMatchingLocationIdentifiers$(zone: ZoneDto) {
        if (!zone.warehouseId || zone.locationIdentifiers.length === 0) {
            this.logger.error('Zone information is misconfigured!');
            return of(zone);
        }

        const zoneType = this.getZoneTypeOfZone(zone);
        const currentZone = this.getById(zone.id);
        if (!zoneType || !currentZone) {
            this.logger.error('Zone information is misconfigured!');
            return of(zone);
        }

        const zones = this.getZonesWithMatchingLocationIdentifiers(
            zone.warehouseId,
            currentZone.locationIdentifiers
        );
        const zoneUpdates = zones.map(z => {
            z.locationIdentifiers = [
                ...zone.locationIdentifiers,
                ...z.locationIdentifiers.filter(
                    l => asNumberSafe(l.level) > zoneType.level
                ),
            ];
            return this.save$(z, {
                emitEvent: false,
            });
        });
        return forkJoin(zoneUpdates).pipe(
            tap(() => {
                this.updated$.next({
                    entity: zone,
                    actionText: 'updated',
                });
            }),
            map(() => zone)
        );
    }

    private checkZoneAndAddToStore(
        zoneLocation: WarehouseStructure[0],
        parentZone?: ZoneRead
    ) {
        if (zoneLocation.zone) {
            this.store.add(zoneLocation.zone as ZoneRead, false);
        } else {
            const locationIdentifiers = [
                ...asArraySafe(parentZone?.locationIdentifiers),
                {
                    name: zoneLocation.locationName,
                    level: zoneLocation.locationLevel as number,
                },
            ];
            const tempZone = {
                id: getTempId(locationIdentifiersKey(locationIdentifiers)),
                name: zoneLocation.locationName,
                warehouse: { id: zoneLocation.warehouseId },
                warehouseId: zoneLocation.warehouseId,
                locationIdentifiers: locationIdentifiers,
            } as ZoneRead;
            zoneLocation.zone = tempZone;
            this.store.add(tempZone, false);
            zoneLocation.children?.forEach(child => {
                this.checkZoneAndAddToStore(child, tempZone);
            });
        }
    }

    resetStoreForWarehouse(warehouseId: string) {
        const zonesToKeep = this.store.filter(
            (z: ZoneRead) => z.warehouseId !== warehouseId
        );
        this.store.set(zonesToKeep);
    }

    setNodes(structure: WarehouseStructure | null) {
        if (!structure) {
            return;
        }
        // Go through tree recursively and add zone field to store if not null
        structure.forEach(zoneLocation => {
            this.checkZoneAndAddToStore(zoneLocation);
        });
    }

    getNextPriority(
        warehouseId: string,
        parentZone?: ZoneRead | ZoneDto | null
    ): number {
        if (!parentZone) {
            const zones = this.getZonesByPriority(warehouseId);
            return zones.length
                ? asNumberSafe(zones[zones.length - 1].zonePriority) + 1
                : 1;
        }

        const parentZones = this.getZonesWithMatchingLocationIdentifiers(
            warehouseId,
            parentZone.locationIdentifiers
        );

        if (!parentZones.length) {
            this.logger.error(
                'Zones with matching location identifiers not found!'
            );
            return this.getNextPriority(warehouseId);
        }

        return (
            asNumberSafe(parentZones[parentZones.length - 1].zonePriority) + 1
        );
    }

    checkForConflictingZoneInfo(zone: ZoneDto): ConflictingZoneResult {
        const matchingZonesByName = this.store.filter(
            (z: ZoneRead) =>
                z.id !== zone.id &&
                z.warehouseId === zone.warehouseId &&
                !z.isDeleted &&
                locationIdentifiersKey(z.locationIdentifiers) ===
                    locationIdentifiersKey(zone.locationIdentifiers)
        );

        if (matchingZonesByName.length) {
            const zoneType = this.getZoneTypeOfZone(zone);
            const parentZoneType =
                zoneType && this.getZoneTypeByLevel(zoneType.level - 1);
            return {
                error: true,
                message: `A zone with this name already exists in this warehouse${parentZoneType ? ' under this ' + parentZoneType.type : ''}.`,
            };
        }

        return {
            error: false,
            message: '',
        };
    }

    override applyResourceAuth(body: ZoneCreate): void {
        const locationId = this._warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }
}
